import type { FleetV2Response } from '@emma/api_client/types';
import { Button, Icon, Tooltip } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { type DeploymentListQuery, type DeploymentSort, useDeployments } from '@/hooks/deployment';
import { useCanWriteById } from '@/hooks/workspace/use_workspace_access';
import { paths } from '@/utility/global_paths';
import { getDeploymentTableColumns } from '../common/table/column_definitions/Deployment';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { CreateDeploymentDialog } from './CreateDeploymentDialog';

type Props = {
  fleet: FleetV2Response;
};

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  applicationVersion: 'applicationVersionIdIn',
  createdAt: ['createdAfter', 'createdBefore'],
};

const deploymentDescription =
  'Each deployment represents a versioned snapshot of an application configuration (Docker Compose) that is pushed to devices in this fleet';

const DeploymentTableContent = ({ fleet }: Props) => {
  const columns = useMemo(() => getDeploymentTableColumns(fleet.workspaceId), [fleet.workspaceId]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const canWrite = useCanWriteById(fleet.workspaceId);
  const { pagination, sorting, filters } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);

  const queryParams: DeploymentListQuery = {
    ...serverTableParams<DeploymentSort>(pagination, sorting),
    ...buildFilterParams(debouncedFilters, FILTERS),
  };

  const { data, isLoading, mutate, isValidating } = useDeployments(
    fleet.workspaceId,
    fleet.id,
    queryParams
  );

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <>
      <Table
        addButton={
          canWrite
            ? () => (
                <Tooltip label="Create a new deployment" placement="left">
                  <Button
                    borderRightRadius="0px"
                    leftIcon={<Icon as={PlusBold} />}
                    onClick={() => setIsCreateDialogOpen(true)}
                    size="sm"
                    variant="outline"
                  >
                    Deploy
                  </Button>
                </Tooltip>
              )
            : undefined
        }
        columns={columns}
        data={data?.deployments || []}
        description={deploymentDescription}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        rowLink={({ id }) => paths.deploymentById(fleet.workspaceId, fleet.id, id)}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Deployments"
      />
      <CreateDeploymentDialog
        fleetId={fleet.id}
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        workspaceId={fleet.workspaceId}
      />
    </>
  );
};

export const DeploymentTable = (props: Props) => (
  <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="Deployments">
    <DeploymentTableContent {...props} />
  </TableStoreProvider>
);
